// Termin-buchen-Dialog (inkl. wiederkehrender Termine) —
// extrahiert aus AppointmentsPage.js.
import React from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { CustomerSearchSelect } from '@/components/ui/customer-search-select';
import { getAdminOnlySlotInfo } from '@/utils/trainerSlotLogic';
import { toLocalYMD } from './calendarUtils';

export const BookingDialog = ({
  open, onOpenChange, selectedTrainer, selectedSlot, currentDate,
  customTime, setCustomTime, bookingData, setBookingData,
  services, availableServices, availableServicesInfo, customers, isAddingParticipant,
  isRecurring, setIsRecurring, recurringFrequency, setRecurringFrequency,
  recurringOccurrences, setRecurringOccurrences, isBooking, onSubmit, onCancel,
  notifyCustomer = true, setNotifyCustomer,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="booking-dialog-content">
      <DialogHeader>
        <DialogTitle>Termin buchen</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        {selectedTrainer && selectedSlot && (
          <div className="p-3 bg-teal-50 rounded-lg">
            <div className="text-sm font-medium text-teal-900">
              {selectedTrainer.trainer.first_name} {selectedTrainer.trainer.last_name}
            </div>
            <div className="text-sm text-teal-700">
              {currentDate.toLocaleDateString('de-DE', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          </div>
        )}

        {(() => {
          if (!selectedTrainer || !currentDate) return null;
          const dateStr = toLocalYMD(currentDate);
          const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
          const info = getAdminOnlySlotInfo(selectedTrainer, dateStr, dayName, customTime || '00:00');
          if (!info) return null;
          const msg =
            info.reason === 'expired'
              ? `Kundenfenster${info.validUntil ? ` bis ${info.validUntil}` : ''} abgelaufen`
              : info.reason === 'not_yet'
              ? `Für Kunden erst ab ${info.validFrom} buchbar`
              : 'Für Kunden ausgeblendet';
          return (
            <div
              className="flex items-start gap-2 p-3 bg-amber-100 border border-amber-300 rounded-lg text-sm text-amber-900"
              data-testid="booking-admin-only-hint"
            >
              <span>🔒</span>
              <span>
                <b>Achtung:</b> {msg} – dieses Zeitfenster ist aktuell <b>nur für Admin/Trainer</b> buchbar. Du legst den Termin bewusst ausserhalb des Kundenfensters an.
              </span>
            </div>
          );
        })()}

        <div className="space-y-2">
          <Label htmlFor="custom-time">Startzeit (anpassbar)</Label>
          <div className="flex gap-2">
            <Select
              value={customTime.split(':')[0] || '06'}
              onValueChange={(hour) => {
                const minute = customTime.split(':')[1] || '00';
                setCustomTime(`${hour}:${minute}`);
              }}
              modal={false}
            >
              <SelectTrigger className="w-24">
                <SelectValue placeholder="Std" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={5}>
                {Array.from({length: 15}, (_, i) => i + 6).map(hour => (
                  <SelectItem key={hour} value={hour.toString().padStart(2, '0')}>
                    {hour.toString().padStart(2, '0')} Uhr
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={customTime.split(':')[1] || '00'}
              onValueChange={(minute) => {
                const hour = customTime.split(':')[0] || '06';
                setCustomTime(`${hour}:${minute}`);
              }}
              modal={false}
            >
              <SelectTrigger className="w-24">
                <SelectValue placeholder="Min" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={5}>
                <SelectItem value="00">00</SelectItem>
                <SelectItem value="15">15</SelectItem>
                <SelectItem value="30">30</SelectItem>
                <SelectItem value="45">45</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center px-3 py-2 bg-gray-100 rounded-md text-sm font-medium">
              {customTime}
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Zeiten in 15-Minuten-Schritten: :00, :15, :30, :45
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="service">Service auswählen</Label>
            <Select
              value={bookingData.service_id}
              onValueChange={(value) => {
                const svc = availableServices.find((s) => s.id === value);
                const _opts = (svc && Array.isArray(svc.variant_options) && svc.variant_options.length > 0)
                  ? svc.variant_options.map((o) => o.name)
                  : ((svc && Array.isArray(svc.variants)) ? svc.variants : []);
                setBookingData({
                  ...bookingData,
                  service_id: value,
                  // Service passt nicht ganz in die Lücke → Dauer automatisch
                  // auf die maximal freie Zeit kürzen (Admin kann anpassen)
                  duration_override: svc && svc.fits === false
                    ? Math.floor((svc.max_fit_minutes || 0) / 5) * 5
                    : null,
                  // FLEX-Variante: bei genau 1 Variante automatisch wählen, sonst zurücksetzen
                  selected_variant: _opts.length === 1 ? _opts[0] : null,
                });
              }}
              modal={false}
            >
              <SelectTrigger id="service" data-testid="booking-service-select" className="w-full overflow-hidden" disabled={isAddingParticipant}>
                <SelectValue placeholder="Service wählen..." />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={5}>
                {availableServices.map((service) => (
                  <SelectItem key={service.id} value={service.id} textValue={service.name}>
                    <span className="truncate">{service.name}</span>
                    <span className="ml-2 text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                        {service.duration_minutes} Min | {service.points_cost || 0} Pkt | CHF {service.price.toFixed(2)}
                    </span>
                    {service.fits === false && (
                      <span className="ml-2 text-xs text-orange-600 whitespace-nowrap flex-shrink-0">
                        ⚠ nur {service.max_fit_minutes} Min frei
                      </span>
                    )}
                  </SelectItem>
                ))}

                {availableServices.length === 0 && (
                  <div className="p-2 text-sm text-gray-500 text-center">
                    Keine Services verfügbar
                  </div>
                )}
              </SelectContent>
            </Select>

          {/* Info message if services are filtered — 07/2026 enhanced UX */}
          {availableServices.length > 0 && availableServices.length < services.length && (
            <div className="mt-2 rounded-md bg-orange-50 border border-orange-200 p-2.5 text-xs" data-testid="services-filtered-info">
              <p className="text-orange-700 font-medium">
                ⚠️ Nur {availableServices.length} von {services.length} Services verfügbar zu dieser Zeit
              </p>
              {availableServicesInfo?.max_bookable_minutes != null && (
                <p className="text-orange-600 mt-1">
                  Nur <strong>{availableServicesInfo.max_bookable_minutes} Minuten</strong> frei bis zum nächsten Termin oder Blockade.
                </p>
              )}
              {availableServicesInfo?.filtered_out?.length > 0 && (
                <details className="mt-1.5 cursor-pointer">
                  <summary className="text-orange-700 hover:text-orange-900 select-none">
                    Warum sind {availableServicesInfo.filtered_out.length} Services ausgeblendet?
                  </summary>
                  <ul className="mt-1 ml-4 list-disc text-orange-600 space-y-0.5">
                    {availableServicesInfo.filtered_out.slice(0, 6).map((svc) => (
                      <li key={svc.id}>
                        <strong>{svc.name}</strong> ({svc.duration_minutes || '?'} Min) — passt nicht in die verfügbare Lücke.
                      </li>
                    ))}
                    {availableServicesInfo.filtered_out.length > 6 && (
                      <li className="italic">…und {availableServicesInfo.filtered_out.length - 6} weitere.</li>
                    )}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        {/* FLEX-Varianten (z.B. EMS oder Personal Training) — 06/2026:
            Admin wählt beim Buchen die Variante, analog Kundenportal/App. */}
        {(() => {
          const svc =
            (availableServices || []).find((s) => s.id === bookingData.service_id) ||
            (services || []).find((s) => s.id === bookingData.service_id);
          const vOpts = svc && Array.isArray(svc.variant_options) ? svc.variant_options : [];
          const legacy = svc && Array.isArray(svc.variants) ? svc.variants : [];
          // Beauty-Varianten (variant_options) haben Aufpreis/Zusatzzeit; sonst
          // Fallback auf einfache String-Varianten (FLEX: EMS/Personal Training).
          const variantList = vOpts.length > 0
            ? vOpts
            : legacy.map((n) => ({ name: n, price_delta: 0, duration_delta_minutes: 0 }));
          if (variantList.length === 0) return null;
          const basePrice = svc ? Number(svc.price || 0) : 0;
          const baseDur = svc ? Number(svc.duration_minutes || 0) : 0;
          const selOpt = variantList.find((v) => v.name === bookingData.selected_variant);
          return (
            <div className="space-y-2" data-testid="booking-variant-section">
              <Label>Variante / Zusatzleistung wählen</Label>
              <div className="flex flex-wrap gap-2">
                {variantList.map((v) => {
                  const isSel = bookingData.selected_variant === v.name;
                  const pd = Number(v.price_delta || 0);
                  const dd = Number(v.duration_delta_minutes || 0);
                  return (
                    <button
                      key={v.name}
                      type="button"
                      data-testid={`booking-variant-${v.name}`}
                      onClick={() => setBookingData({ ...bookingData, selected_variant: v.name })}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border-2 ${
                        isSel
                          ? 'bg-teal-500 text-white border-teal-600 shadow'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-teal-400'
                      }`}
                    >
                      {isSel && '✓ '}{v.name}
                      {(pd !== 0 || dd !== 0) && (
                        <span className={`ml-1 text-xs ${isSel ? 'text-teal-50' : 'text-gray-500'}`}>
                          {pd !== 0 && `+CHF ${pd.toFixed(2)}`}
                          {pd !== 0 && dd !== 0 && ' · '}
                          {dd !== 0 && `+${dd} Min`}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {selOpt && (Number(selOpt.price_delta || 0) !== 0 || Number(selOpt.duration_delta_minutes || 0) !== 0) && (
                <p className="text-xs text-teal-700 font-medium" data-testid="booking-variant-effective">
                  Preis: CHF {(basePrice + Number(selOpt.price_delta || 0)).toFixed(2)} · Dauer: {baseDur + Number(selOpt.duration_delta_minutes || 0)} Min
                </p>
              )}
              {!bookingData.selected_variant && (
                <p className="text-xs text-amber-600" data-testid="booking-variant-hint">
                  Bitte Variante wählen.
                </p>
              )}
            </div>
          );
        })()}

        {/* 08/2026: Dauer anpassbar (nur Admin/Trainer-Kalender — Kunden-Slots
            im Portal bleiben unverändert beim Service-Standard) */}
        {bookingData.service_id && (
          <div className="space-y-2">
            <Label htmlFor="duration-override">Dauer (Minuten)</Label>
            <div className="flex items-center gap-2">
              <Input
                id="duration-override"
                type="number"
                min={10}
                max={240}
                step={5}
                className="w-28"
                value={bookingData.duration_override ?? (services.find((s) => s.id === bookingData.service_id)?.duration_minutes || '')}
                onChange={(e) => setBookingData({ ...bookingData, duration_override: e.target.value })}
                data-testid="booking-duration-input"
              />
              {bookingData.duration_override &&
                parseInt(bookingData.duration_override) !== (services.find((s) => s.id === bookingData.service_id)?.duration_minutes || 0) && (
                <button
                  type="button"
                  className="text-xs text-teal-600 hover:underline"
                  onClick={() => setBookingData({ ...bookingData, duration_override: null })}
                  data-testid="booking-duration-reset"
                >
                  Standard ({services.find((s) => s.id === bookingData.service_id)?.duration_minutes} Min)
                </button>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="customer">Kunde auswählen</Label>
          <CustomerSearchSelect
            customers={customers}
            value={bookingData.customer_id}
            onValueChange={(value) => setBookingData({ ...bookingData, customer_id: value })}
            placeholder="Kunde suchen..."
          />
          {customers.find(c => c.id === bookingData.customer_id)?.status === 'inactive' && (
            <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800" data-testid="inactive-customer-warning">
              ⚠️ <strong>Dieser Kunde ist inaktiv.</strong> Beim Buchen wird er automatisch
              wieder <strong>aktiviert</strong> und erhält eine „Willkommen zurück"-Nachricht
              per E-Mail und WhatsApp.
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes">Notizen (optional)</Label>
          <Textarea
            id="notes"
            placeholder="Notizen zum Termin..."
            value={bookingData.notes}
            onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
            rows={3}
          />
        </div>

        {/* Recurring Appointment Section */}
        <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <Label htmlFor="recurring" className="text-sm font-medium">
              Wiederkehrenden Termin erstellen
            </Label>
            <Switch
              id="recurring"
              checked={isRecurring}
              onCheckedChange={(checked) => {
                setIsRecurring(checked);
                if (!checked) {
                  // Reset values when disabled
                  setRecurringFrequency('weekly');
                  setRecurringOccurrences(10);
                }
              }}
            />
          </div>

          {isRecurring && (
            <div className="space-y-3 mt-3">
              <div className="space-y-2">
                <Label htmlFor="frequency" className="text-sm">Frequenz</Label>
                <Select
                  value={recurringFrequency}
                  onValueChange={setRecurringFrequency}
                >
                  <SelectTrigger id="frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="daily">Täglich</SelectItem>
                    <SelectItem value="weekly">Wöchentlich</SelectItem>
                    <SelectItem value="biweekly">Alle 2 Wochen</SelectItem>
                    <SelectItem value="monthly">Monatlich</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="occurrences" className="text-sm">Anzahl Termine</Label>
                <Input
                  id="occurrences"
                  data-testid="recurring-occurrences-input"
                  type="number"
                  min="1"
                  max="300"
                  value={recurringOccurrences}
                  onChange={(e) => {
                    const v = e.target.value;
                    setRecurringOccurrences(v === '' ? '' : parseInt(v, 10) || '');
                  }}
                  onBlur={() => {
                    const n = parseInt(recurringOccurrences, 10);
                    setRecurringOccurrences(!n || n < 1 ? 1 : Math.min(300, n));
                  }}
                />
              </div>

              {/* Preview */}
              <div className="p-2 bg-white rounded text-xs text-gray-600">
                <strong>Vorschau:</strong> Es werden {parseInt(recurringOccurrences, 10) || 1} Termine erstellt
                {recurringFrequency === 'daily' && ` (täglich)`}
                {recurringFrequency === 'weekly' && ` (jede Woche)`}
                {recurringFrequency === 'biweekly' && ` (alle 2 Wochen)`}
                {recurringFrequency === 'monthly' && ` (jeden Monat)`}
              </div>
            </div>
          )}
        </div>

        {/* Kunden-Benachrichtigung (E-Mail & WhatsApp) — Standard AN */}
        <div className={`rounded-lg border p-3 ${notifyCustomer ? 'border-teal-200 bg-teal-50' : 'border-gray-300 bg-gray-100'}`} data-testid="notify-customer-box">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label htmlFor="notify-customer-switch" className="text-sm font-semibold cursor-pointer">
                {notifyCustomer ? '📨 Kunde benachrichtigen' : '🔕 Ohne Benachrichtigung buchen'}
              </Label>
              <p className="text-xs text-gray-600 mt-0.5">
                {notifyCustomer
                  ? 'E-Mail & WhatsApp werden versendet (je nach den Einstellungen des Kunden).'
                  : 'Es wird KEINE E-Mail/WhatsApp versendet – ideal bei vielen Umbuchungen.'}
              </p>
            </div>
            <Switch
              id="notify-customer-switch"
              checked={notifyCustomer}
              onCheckedChange={(v) => setNotifyCustomer && setNotifyCustomer(v)}
              data-testid="notify-customer-switch"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            Abbrechen
          </Button>
          <Button
            onClick={onSubmit}
            className="flex-1"
            disabled={isBooking}
            data-testid="booking-submit-btn"
          >
            {isBooking ? 'Wird gebucht...' : 'Termin buchen'}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
